<template>
  <v-container-np id="freeconsultation">
    <v-container-restricted
      style="max-width: 1200px !important"
      class="text-center d-flex flex-column align-center"
      :class="Spacings.homePageSectionPadding"
    >
      <underlined-heading
        :text="$t('taHome.getFreeConsultation')"
      ></underlined-heading>

      <v-row
        no-gutters
        justify="center"
        class="w-100"
        :class="Spacings.homePageHeadingMargin"
      >
        <v-col
          cols="12"
          sm="6"
          class="text-center text-sm-left mb-5 pr-sm-5 pr-md-10"
        >
          <slide-on-intersect-container direction="left">
            <h5 class="text-h6 text-sm-h5 mb-sm-3 mb-md-5">
              Send Us a Free Message
            </h5>
            <p class="text-subtitle-1 d-none d-sm-inline">
              Are you ready to harness the power of AI, data analytics, and
              machine learning to drive your business forward? Whether you have
              a specific project in mind or need guidance on the best solutions
              for your unique challenges, our team of experts is here to help.
              Fill out the form below to schedule your free consultation.
            </p>
          </slide-on-intersect-container>
        </v-col>
        <v-col cols="12" sm="6" class="text-left">
          <v-form ref="form" @submit.prevent="onSubmit">
            <slide-on-intersect-container direction="right">
              <v-text-field
                @click:control="resetNameValidation"
                ref="nameField"
                v-model:model-value="name"
                :rules="[nameValidator]"
                placeholder="Name"
                variant="outlined"
              ></v-text-field>
            </slide-on-intersect-container>
            <slide-on-intersect-container direction="right">
              <v-text-field
                @click:control="resetEmailValidation"
                ref="emailField"
                v-model:model-value="email"
                :rules="[emailValidator]"
                placeholder="Email"
                variant="outlined"
              ></v-text-field>
            </slide-on-intersect-container>
            <slide-on-intersect-container direction="right">
              <v-text-field
                placeholder="Phone Number"
                variant="outlined"
                ref="phoneField"
                @click:control="resetPhoneValidation"
                v-model:model-value="phone"
                :rules="[phoneValidator]"
              ></v-text-field>
            </slide-on-intersect-container>
            <slide-on-intersect-container direction="right">
              <v-text-field
                placeholder="Subject"
                variant="outlined"
                ref="subjectField"
                @click:control="resetSubjectValidation"
                v-model:model-value="subject"
                :rules="[subjectValidator]"
              ></v-text-field>
            </slide-on-intersect-container>
            <slide-on-intersect-container direction="right">
              <v-textarea
                color="primary"
                ref="textAreaField"
                @click:control="resetTextAreaValidation"
                v-model:model-value="text"
                :rules="[textAreaValidator]"
                placeholder="Message"
                variant="outlined"
                :counter="maxTextCharacters"
              ></v-textarea>
            </slide-on-intersect-container>
            <slide-on-intersect-container direction="bottom">
              <v-row no-gutters justify="center"
                ><v-btn :loading="isLoading" type="submit"
                  >Send Message</v-btn
                ></v-row
              >
            </slide-on-intersect-container>
          </v-form>
        </v-col>
      </v-row>
    </v-container-restricted>
  </v-container-np>
</template>
<script setup lang="ts">
import { getEmailFieldLogic } from "~/composables/common/inputfields/email_field";
import { getNameFieldLogic } from "~/composables/common/inputfields/name_field";
import { getRequiredTextFieldLogic } from "~/composables/common/inputfields/required_field";
import { getTextAreaLogic } from "~/composables/common/inputfields/text_area";
import getSendUsAMessageLogic from "~/composables/send_us_a_message_logic";
import { Spacings } from "~/core/constants/vuetify-class-constants/spacings";
import { DialogType, SnackbarType } from "~/core/enums";
import type {
  actionDialogProvideFunctions,
  contactUsMessage,
  snackbarProvideFunctions,
} from "~/core/interfaces/common";
import { actionDialogKey, snackbarKey } from "~/core/keys";
const { name, nameField, nameValidator, resetNameValidation } =
  getNameFieldLogic();
const { email, emailField, emailValidator, resetEmailValidation } =
  getEmailFieldLogic();

const {
  maxTextCharacters,
  resetTextAreaValidation,
  text,
  textAreaField,
  textAreaValidator,
} = getTextAreaLogic();

const {
  requiredValidator: phoneValidator,
  requiredText: phone,
  requiredTextField: phoneField,
  resetRequiredTextValidation: resetPhoneValidation,
} = getRequiredTextFieldLogic();

const {
  requiredValidator: subjectValidator,
  requiredText: subject,
  requiredTextField: subjectField,
  resetRequiredTextValidation: resetSubjectValidation,
} = getRequiredTextFieldLogic();

const { isLoading, sendUsAMessage } = getSendUsAMessageLogic();
const { showMessageDialogFunc } = inject(
  actionDialogKey
) as actionDialogProvideFunctions;
const { showSnackbarFunc } = inject(snackbarKey) as snackbarProvideFunctions;
const form = ref();
function onSubmit() {
  if (form.value.isValid) {
    const messageInfo: contactUsMessage = {
      email: email.value,
      phone: phone.value,
      message: text.value,
      name: name.value,
      subject: subject.value,
    };

    sendUsAMessage(messageInfo, onSendSuccess, onSendFailure);
  }

  function onSendSuccess() {
    showMessageDialogFunc(
      "Thank you for reaching out to us. We've sent a copy of your message to your email. Check the spam folder if it doesn't appear in your inbox.",
      DialogType.success
    );
    email.value = "";
    phone.value = "";
    text.value = "";
    subject.value = "";
    name.value = "";
  }

  function onSendFailure(message: string) {
    showSnackbarFunc(message, SnackbarType.error);
  }
}
</script>
